"use client";

import React from "react";
import { Loader2, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function QuickQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="58644" id="kontaktformular" className="relative w-full py-20 bg-secondary text-secondary-foreground overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:16px_16px]" />

      <div ref={ref} className={`flex flex-col items-center relative z-10 transition-all duration-400 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center flex flex-col items-center">
            <h1 className="mb-6 font-serif text-4xl font-semibold text-white sm:text-5xl md:text-6xl lg:text-[64px] leading-tight tracking-tight">ANFRAGE STARTEN</h1>
            <p className="mb-10 max-w-2xl font-sans text-lg text-white/90 md:text-xl tracking-normal">Sichern Sie sich jetzt ihr unverbindliches Angebot.</p>
          </div>

          <div className="bg-card/80 backdrop-blur-sm border border-border rounded-none p-6 md:p-8 shadow-2xl">
            <FormProvider formKey="contact_form_kontaktanfrage" formId="contact_form_kontaktanfrage" sectionName="quick-quote" defaultValues={{
            name: "",
            email: "",
            phone: "",
            service: "",
            budget: "",
            details: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            service: {
              required: true
            },
            budget: {
              min: 0
            },
            details: {
              required: false
            }
          }} submitText="ANFRAGE ABSENDEN" successMessage="Ihre Anfrage wurde erfolgreich übermittelt. Wir melden uns in Kürze.">
              <QuickQuoteForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function QuickQuoteForm() {
  const {
    control,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/20 rounded-none flex items-center justify-center mb-2">
          <CheckCircle2 className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Anfrage Erhalten!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto font-default">{successMessage}</p>
      </div>;
  }
  const inputStyles = "bg-background border-border text-foreground rounded-none placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary transition-all duration-300";
  const labelStyles = "text-muted-foreground font-mono text-xs uppercase tracking-wider";
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Lade-Postleitzahl</FormLabel>
            <FormControl>
              <Input placeholder="z.B. 10115" {...field} className={inputStyles} />
            </FormControl>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Kontakt E-Mail</FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@beispiel.de" {...field} className={inputStyles} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Telefonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+49 123 456789" {...field} className={inputStyles} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="service" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Fahrzeugtyp / Service</FormLabel>
              <FormControl>
                <select {...field} className={`flex h-9 w-full rounded-none border px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 ${inputStyles}`}>
                  <option value="" disabled className="bg-background text-muted-foreground">
                    Bitte wählen
                  </option>
                  {["Kurierfahrt", "Expressversand", "Antiquitätentransport", "Umzug & Räumung"].map(option => <option key={option} value={option} className="bg-background text-foreground">
                      {option}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>
                Gesamtgewicht (kg)
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="z.B. 250" {...field} className={inputStyles} min={0} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Maße & Entlade-PLZ</FormLabel>
            <FormControl>
              <textarea placeholder="Bitte geben Sie hier die Ziel-PLZ sowie Maße und Besonderheiten der Ladung an..." {...field} rows={4} className={`flex w-full rounded-none border px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 ${inputStyles}`} />
            </FormControl>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-none flex items-center gap-2 text-destructive text-sm font-default">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 mt-2 bg-primary hover:bg-primary/90 text-primary-foreground font-serif font-bold uppercase tracking-widest transition-all duration-150 border-none rounded-none">
        {isSubmitting ? <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            VERARBEITUNG...
          </> : <>
            ANFRAGE ABSENDEN
            <Send className="ml-2 h-4 w-4" />
          </>}
      </Button>
    </Form>;
}